/**
 * Viewport metrics bootstrap
 *
 * Maintains CSS variables that react to dynamic viewport changes like the
 * address bar and the soft keyboard. Focuses on layout stability by tracking a
 * session-stable minimum visible height and approximating a keyboard inset.
 *
 * Exports:
 * - setupViewportMetrics(): () => void – installs listeners and returns cleanup.
 */

import type { PluginListenerHandle } from '@capacitor/core';
import { devLog, devWarn } from './env';

/** Install viewport listeners and return a cleanup function. */
export function setupViewportMetrics(): () => void {
  // Track a session-stable minimum visible height to avoid layout jumps.
  let minSeenHeight = Number.POSITIVE_INFINITY;
  let lastOrientation = window.innerWidth <= window.innerHeight ? 'portrait' : 'landscape';
  const docEl = document.documentElement;

  const isTextInputFocused = () => {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || el.isContentEditable;
  };

  const computeHeights = () => {
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    const vvHeight = Math.max(0, vv?.height || 0);
    const vvWidth = Math.max(0, vv?.width || 0);
    const hasVV = !!vv && vvHeight > 0;
    const measuredVvh = hasVV ? vvHeight : window.innerHeight;
    const measuredVvw = hasVV ? vvWidth : window.innerWidth;
    return { hasVV, measuredVvh, measuredVvw, vv };
  };

  const update = (reason: string) => {
    try {
      // Avoid reacting to external overlays when our app is not visible.
      if (document.visibilityState !== 'visible') return;
      // Use platform defaults; do not clamp platform-specific insets here.
      const { hasVV, measuredVvh } = computeHeights();

      // Reset the minimum on orientation change.
      const currentOrientation = window.innerWidth <= window.innerHeight ? 'portrait' : 'landscape';
      if (currentOrientation !== lastOrientation) {
        lastOrientation = currentOrientation;
        minSeenHeight = Number.POSITIVE_INFINITY;
      }

      // Track the minimum visible height seen this session (svh-like stability).
      minSeenHeight = Math.min(minSeenHeight, measuredVvh);
      const stableVvh = isFinite(minSeenHeight) ? minSeenHeight : measuredVvh;

      // Expose a stable dynamic viewport height in CSS (pixels), so
      // consumers using var(--vvh) avoid overlapping system UI like
      // navigation and URL bars.
      try {
        document.documentElement.style.setProperty('--vvh', `${Math.round(stableVvh)}px`);
      } catch {}

      // Approximate keyboard inset only when a text input is focused and
      // the visual viewport shrinks significantly (avoid address-bar noise).
      let keyboardInset = 0;
      const localFocus = document.hasFocus() && isTextInputFocused();
      if (hasVV && localFocus) {
        const diff = Math.max(0, window.innerHeight - measuredVvh);
        if (diff > 100) keyboardInset = diff; // heuristic threshold
      }
      docEl.style.setProperty('--keyboard-inset', `${Math.round(keyboardInset)}px`);

      // Do not compute platform-specific navbar height; rely on env(safe-area-*) only.

      devLog(`[Viewport] ${reason}: measured=${measuredVvh}, stable=${stableVvh}, keyboard=${keyboardInset}`);
    } catch (e) {
      // Fallbacks are provided in CSS via var(--vvh)
      devWarn('[Viewport] Update failed:', e);
    }
  };

  // Initial update
  update('init');

  // VisualViewport event handlers (keep references for proper cleanup)
  const vv = (window as any).visualViewport as VisualViewport | undefined;
  const onVVResize = () => update('vv-resize');
  const onVVScroll = () => update('vv-scroll');
  if (vv) {
    vv.addEventListener('resize', onVVResize);
    vv.addEventListener('scroll', onVVScroll);
  }

  // Window + document event handlers (named for cleanup)
  const onOrientationChange = () => window.setTimeout(() => update('orientation'), 100);
  const onWindowResize = () => update('window-resize');
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') window.setTimeout(() => update('visibility-visible'), 100);
  };
  const onWindowFocus = () => {
    // Nudge all page-frame scrollers back to their natural top anchoring
    try {
      document.querySelectorAll<HTMLElement>('.page-frame').forEach((el) => {
        if (el.scrollTop !== 0) el.scrollTop = 0;
      });
    } catch {}
    window.setTimeout(() => update('window-focus'), 60);
  };

  window.addEventListener('orientationchange', onOrientationChange);
  window.addEventListener('resize', onWindowResize);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onWindowFocus);

  // Capacitor App state events (if available). We assign the handle so we can remove it.
  let removeAppListener: (() => Promise<void>) | undefined;
  if ((window as any).Capacitor) {
    // Defer import to avoid bundling the plugin unnecessarily on web.
    import('@capacitor/app')
      .then(async ({ App }) => {
        const handle: PluginListenerHandle = await App.addListener('appStateChange', (state) => {
          if (state.isActive) window.setTimeout(() => update('app-active'), 100);
        });
        removeAppListener = handle.remove.bind(handle);
      })
      .catch(() => {
        // Capacitor App plugin not available — safe to ignore on web builds.
      });
  }

  // Cleanup all listeners using the same referenced functions.
  return () => {
    if (vv) {
      vv.removeEventListener('resize', onVVResize);
      vv.removeEventListener('scroll', onVVScroll);
    }
    window.removeEventListener('orientationchange', onOrientationChange);
    window.removeEventListener('resize', onWindowResize);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onWindowFocus);
    if (removeAppListener) void removeAppListener();
  };
}
